import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Expand } from "lucide-react";
import { useSite } from "../context/SiteContext";
import { Reveal, SectionHeader, EASE } from "./Reveal";

const SPANS = [
    "md:col-span-8 md:row-span-2",
    "md:col-span-4 md:row-span-2",
    "md:col-span-4 md:row-span-1",
    "md:col-span-8 md:row-span-1",
    "md:col-span-6 md:row-span-1",
    "md:col-span-6 md:row-span-1",
    "md:col-span-4 md:row-span-1",
    "md:col-span-8 md:row-span-1",
    "md:col-span-8 md:row-span-1",
    "md:col-span-4 md:row-span-1",
    "md:col-span-6 md:row-span-1",
    "md:col-span-6 md:row-span-1",
];

export default function Gallery() {
    const { content, t } = useSite();
    const items = content?.gallery || [];
    const [cat, setCat] = useState("Todos");
    const [lightbox, setLightbox] = useState(-1);

    const categories = ["Todos", ...new Set(items.map((i) => i.category).filter(Boolean))];
    const filtered = cat === "Todos" ? items : items.filter((i) => i.category === cat);
    const catLabel = (c) => t.cats[c] || c;

    useEffect(() => {
        if (lightbox < 0) return;
        const onKey = (e) => {
            if (e.key === "Escape") setLightbox(-1);
            if (e.key === "ArrowRight") setLightbox((v) => (v + 1) % filtered.length);
            if (e.key === "ArrowLeft") setLightbox((v) => (v - 1 + filtered.length) % filtered.length);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [lightbox, filtered.length]);

    return (
        <section id="galeria" data-testid="gallery-section" className="relative py-24 sm:py-32 bg-[#0D0D0F]">
            <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
                <SectionHeader eyebrow={t.gallery.eyebrow} title={t.gallery.title} />

                <Reveal delay={0.15} className="mt-10">
                    <div className="flex flex-wrap gap-2" data-testid="gallery-filter">
                        {categories.map((c) => (
                            <button
                                key={c}
                                data-testid={`gallery-filter-${c.toLowerCase().replace(/\s/g, "-")}`}
                                onClick={() => setCat(c)}
                                className={`px-4 py-2 rounded-full text-xs tracking-widest uppercase transition-all duration-300 border ${
                                    cat === c
                                        ? "bg-gold-gradient text-black border-transparent font-semibold"
                                        : "border-gold/25 text-zinc-300 hover:border-gold/60 hover:text-gold-light"
                                }`}
                            >
                                {c === "Todos" ? t.gallery.all : catLabel(c)}
                            </button>
                        ))}
                    </div>
                </Reveal>

                <div className="mt-12 grid grid-cols-1 md:grid-cols-12 auto-rows-[220px] md:auto-rows-[240px] gap-4">
                    {filtered.map((item, i) => (
                        <motion.button
                            layout
                            key={item.id}
                            initial={{ opacity: 0, scale: 0.96 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true, amount: 0.1 }}
                            transition={{ duration: 0.6, delay: (i % 6) * 0.06, ease: EASE }}
                            onClick={() => setLightbox(i)}
                            data-testid={`gallery-item-${item.id}`}
                            className={`group relative overflow-hidden rounded-2xl border border-gold/10 hover:border-gold/40 transition-colors duration-500 row-span-1 col-span-1 ${SPANS[i % SPANS.length]}`}
                        >
                            <img
                                src={item.url}
                                alt={item.caption || item.category}
                                loading="lazy"
                                className="w-full h-full object-cover transition-transform duration-[1.2s] group-hover:scale-[1.06]"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-60 group-hover:opacity-90 transition-opacity duration-500" />
                            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                                <span className="h-12 w-12 rounded-full glass border border-gold/40 flex items-center justify-center text-gold">
                                    <Expand size={18} />
                                </span>
                            </div>
                            {item.caption && (
                                <div className="absolute bottom-0 inset-x-0 p-4 text-left">
                                    <p className="text-sm text-amber-50 font-light">{item.caption}</p>
                                    <p className="text-[10px] uppercase tracking-[0.25em] text-gold/80">{catLabel(item.category)}</p>
                                </div>
                            )}
                        </motion.button>
                    ))}
                </div>

                {filtered.length === 0 && (
                    <p className="mt-12 text-center text-zinc-500 font-light" data-testid="gallery-empty">
                        {t.gallery.empty}
                    </p>
                )}
            </div>

            <AnimatePresence>
                {lightbox >= 0 && filtered[lightbox] && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[80] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4"
                        onClick={() => setLightbox(-1)}
                        data-testid="gallery-lightbox"
                    >
                        <button
                            data-testid="lightbox-close"
                            className="absolute top-5 right-5 text-zinc-300 hover:text-gold p-2 transition-colors"
                            onClick={() => setLightbox(-1)}
                            aria-label="Cerrar"
                        >
                            <X size={28} />
                        </button>
                        <button
                            data-testid="lightbox-prev"
                            className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-300 hover:text-gold p-2 transition-colors"
                            onClick={(e) => {
                                e.stopPropagation();
                                setLightbox((v) => (v - 1 + filtered.length) % filtered.length);
                            }}
                            aria-label="Anterior"
                        >
                            <ChevronLeft size={36} />
                        </button>
                        <motion.figure
                            key={filtered[lightbox].id}
                            initial={{ opacity: 0, scale: 0.94 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.4, ease: EASE }}
                            className="max-w-4xl w-full"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <img
                                src={filtered[lightbox].url}
                                alt={filtered[lightbox].caption || ""}
                                className="w-full max-h-[80vh] object-contain rounded-xl"
                            />
                            {filtered[lightbox].caption && (
                                <figcaption className="mt-4 text-center text-zinc-300 font-light">
                                    {filtered[lightbox].caption}
                                </figcaption>
                            )}
                        </motion.figure>
                        <button
                            data-testid="lightbox-next"
                            className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-300 hover:text-gold p-2 transition-colors"
                            onClick={(e) => {
                                e.stopPropagation();
                                setLightbox((v) => (v + 1) % filtered.length);
                            }}
                            aria-label="Siguiente"
                        >
                            <ChevronRight size={36} />
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
}
